// app/login/page.tsx
'use client'

import { useState, Suspense } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { motion } from 'framer-motion'
import { auth } from '@/lib/supabase'

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectTo = searchParams.get('redirect') || '/home'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await auth.signIn(email, password)
      router.refresh()
      router.push(redirectTo)
    } catch (err: any) {
      setError(err.message || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[var(--refined-cream)] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[400px]"
      >
        {/* Logo */}
        <Link href="/" className="block text-center mb-10">
          <span className="font-bold text-xl tracking-wide text-[var(--refined-ink)]">
            COHERA<span className="text-accent">.</span>
          </span>
        </Link>

        {/* Login Card */}
        <div className="bg-[var(--refined-paper)] border border-[var(--refined-border)] rounded-xl p-8 shadow-sm">
          <h1 className="font-serif text-2xl font-medium text-[var(--refined-ink)] mb-1">
            Welcome back
          </h1>
          <p className="text-[var(--refined-ink-muted)] text-sm mb-6">
            Sign in to continue to Cohera
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-[0.8rem] font-medium text-[var(--refined-ink-secondary)] mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-[var(--refined-border)] bg-[var(--refined-paper)] text-[var(--refined-ink)] placeholder:text-[var(--refined-ink-muted)] focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-colors"
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-[0.8rem] font-medium text-[var(--refined-ink-secondary)]"
                >
                  Password
                </label>
                <Link
                  href="/forgot-password"
                  className="text-[0.75rem] text-accent hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-[var(--refined-border)] bg-[var(--refined-paper)] text-[var(--refined-ink)] placeholder:text-[var(--refined-ink-muted)] focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-colors"
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-accent text-white font-medium rounded-lg hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[var(--refined-border)] text-center">
            <p className="text-[0.85rem] text-[var(--refined-ink-muted)]">
              Need access?{' '}
              <Link href="/contact" className="text-accent hover:underline font-medium">
                Contact us
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link
            href="/"
            className="text-[0.85rem] text-[var(--refined-ink-muted)] hover:text-[var(--refined-ink)] transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </motion.div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[var(--refined-cream)] flex items-center justify-center">
        <div className="text-[var(--refined-ink-muted)]">Loading...</div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  )
}
